import React from 'react';
import {
  Box,
  Divider,
  SparkContentLoader,
  useMediaQuery,
} from '@sparknz/spark-pattern-library';

import { SectionContainer } from './components';

interface LoaderProps {
  isMobile?: boolean;
  isTablet?: boolean;
  isDesktop?: boolean;
}

const BreadcrumbsRect = ({ isTablet, isDesktop }: LoaderProps) => {
  let rectY = 20;
  let textY = 38;

  if (isDesktop) {
    rectY = 30;
    textY = 48;
  } else if (isTablet) {
    rectY = 28;
    textY = 46;
  }

  return (
    <>
      <rect x="0" y={rectY} rx="6" ry="6" width="50" height="24" />
      <text x="55" y={textY}>
        /
      </text>
      <rect x="64" y={rectY} rx="6" ry="6" width="44" height="24" />
      <text x="112" y={textY}>
        /
      </text>
      <rect x="122" y={rectY} rx="6" ry="6" width="82" height="24" />
    </>
  );
};

const TitleRect = ({ isTablet, isDesktop }: LoaderProps) => {
  if (isDesktop) {
    return <rect x="0" y="95" rx="12" ry="12" width="330" height="55" />;
  }

  if (isTablet) {
    return <rect x="0" y="75" rx="12" ry="12" width="320" height="45" />;
  }

  return <rect x="0" y="55" rx="12" ry="12" width="320" height="45" />;
};

const FilterRect = ({ isTablet, isDesktop }: LoaderProps) => {
  if (isDesktop) {
    return (
      <>
        <rect x="0" y="190" rx="24" ry="24" width="200" height="50" />
        <rect x="215" y="190" rx="24" ry="24" width="150" height="50" />

        <rect x="380" y="190" rx="24" ry="24" width="150" height="50" />
        <rect x="540" y="200" rx="6" ry="6" width="120" height="24" />
        <rect x="886" y="190" rx="12" ry="12" width="239" height="49" />
      </>
    );
  }

  if (isTablet) {
    return (
      <>
        <rect x="0" y="160" rx="24" ry="24" width="200" height="50" />
        <rect x="215" y="160" rx="24" ry="24" width="150" height="50" />

        <rect x="380" y="160" rx="24" ry="24" width="150" height="50" />
        <rect x="540" y="175" rx="6" ry="6" width="120" height="24" />
      </>
    );
  }

  return (
    <>
      <rect x="0" y="135" rx="24" ry="24" width="200" height="42" />
      <rect x="215" y="135" rx="24" ry="24" width="150" height="42" />
    </>
  );
};

const SortLoader = ({ isMobile, isTablet }: LoaderProps) => {
  return (
    <SparkContentLoader display="block" height="50px" width="100%">
      {isMobile ? (
        <rect x="0" y="0" rx="5" ry="5" width="100%" height="100%" />
      ) : null}
      {isTablet ? (
        <rect x="66%" y="0" rx="5" ry="5" width="240" height="100%" />
      ) : null}
    </SparkContentLoader>
  );
};

const CardLoader = ({ width = '100%' }: { width: string }) => {
  return (
    <SparkContentLoader
      data-testid="card-loader"
      display="block"
      width={width}
      height="539px"
      overflow="hidden"
      style={{
        boxShadow: '0 2px 8px 0 rgba(29, 29, 29, 0.28)',
        borderRadius: '5px',
      }}
    >
      <rect x="0" y="0" rx="6" ry="6" width="100%" height="260" />
      <rect x="7%" y="270" rx="6" ry="6" width="67%" height="20" />
      <rect x="7%" y="303" rx="6" ry="6" width="20%" height="20" />
      <rect x="7%" y="327" rx="6" ry="6" width="35%" height="25" />
      <rect x="7%" y="365" rx="6" ry="6" width="86%" height="100" />
      <rect x="7%" y="485" rx="6" ry="6" width="60%" height="20" />
    </SparkContentLoader>
  );
};

const DeviceGalleryLoader = () => {
  const isMobile = useMediaQuery({ max: 'lil' });
  const isTablet = useMediaQuery({ min: 'slim', max: 'big' });
  const isDesktop = useMediaQuery({ min: 'big' });

  const props = { isMobile, isTablet, isDesktop };

  let params = {
    containerHeight: '185px',
    cardsCount: 2,
    cardWidth: '100%',
  };

  if (isDesktop) {
    params = {
      ...params,
      containerHeight: '254px',
      cardsCount: 5,
      cardWidth: 'calc(32.5% - 8px)',
    };
  } else if (isTablet) {
    params = {
      ...params,
      containerHeight: '226px',
      cardsCount: 3,
      cardWidth: 'calc(50% - 8px)',
    };
  }

  return (
    <SectionContainer title="Loading Device Gallery...">
      <SparkContentLoader
        display="block"
        height={params.containerHeight}
        width="100%"
      >
        <BreadcrumbsRect {...props} />
        <TitleRect {...props} />
        <FilterRect {...props} />
      </SparkContentLoader>

      <Box mb={['s3', 's2', 's6']}>
        <Divider />
      </Box>

      {!isDesktop && (
        <Box mb="s3">
          <SortLoader {...props} />
        </Box>
      )}

      <Box
        flexDirection="row"
        flexWrap="wrap"
        pt={['s0', 's4', 's2']}
        gap={['s2', 's2', 's3']}
      >
        {new Array(params.cardsCount).fill(null).map((_, i) => (
          <CardLoader key={i.toString()} width={params.cardWidth} />
        ))}
      </Box>
    </SectionContainer>
  );
};

export default DeviceGalleryLoader;
